import type { NextStepCard } from "@/schemas/sections";

/*
 * Card palette for the next-steps section. One consumer today; kept in its own
 * module because 80 lines of colour maps at the top of a component file is what
 * pushed the real layout past the first screen, not because anything else needs
 * to import it.
 */

export const DEFAULT_EMOJI: Record<NextStepCard["kind"], string> = {
  webinar: "🎓",
  explore: "🧭",
  telegram: "✈️",
  custom: "✨",
};

export type Tone = NonNullable<NextStepCard["tone"]>;

// Vibrant colorways mirroring the FAQ carousel (FaqCarouselSection) brand
// palette so the upsell cards read as colorful, not flat white.
export const TONE_STYLES: Record<
  Tone,
  { card: string; title: string; body: string; chip: string; badge: string; cta: string }
> = {
  purple: {
    card: "bg-brand",
    title: "text-brand-content",
    body: "text-brand-content/80",
    chip: "bg-brand-content/15",
    badge: "bg-brand-content/90 text-brand",
    cta: "text-brand-content",
  },
  peach: {
    card: "bg-(--tone-peach-bg)",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-fg/10",
    badge: "bg-fg/15 text-fg",
    cta: "text-brand",
  },
  mint: {
    card: "bg-(--tone-mint-bg)",
    title: "text-(--tone-mint-ink)",
    body: "text-(--tone-mint-muted)",
    chip: "bg-(--tone-mint-ink)/10",
    badge: "bg-(--tone-mint-ink)/15 text-(--tone-mint-ink)",
    cta: "text-(--tone-mint-ink)",
  },
  coral: {
    card: "bg-(--tone-coral-bg)",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-fg/10",
    badge: "bg-fg/15 text-fg",
    cta: "text-brand",
  },
  blue: {
    card: "bg-(--tone-blue-bg)",
    title: "text-(--tone-blue-ink)",
    body: "text-(--tone-blue-muted)",
    chip: "bg-(--tone-blue-ink)/10",
    badge: "bg-(--tone-blue-ink)/15 text-(--tone-blue-ink)",
    cta: "text-(--tone-blue-ink)",
  },
  lavender: {
    card: "bg-surface-brand",
    title: "text-fg",
    body: "text-muted",
    chip: "bg-brand/12",
    badge: "bg-brand/15 text-fg",
    cta: "text-brand",
  },
};

// Component-family "content-on-color" tone palette — mirrors FaqCarouselSection.tsx
// (this component's sibling and, alphabetically, the canonical definition site)
// verbatim: same custom-property names + values. No shared CSS file the two
// TSX-only components both import exists yet, so each defines its own copy via
// inline `style` on its root wrapper. Hoist to tokens/semantic.css (or a shared
// tone-tokens.css) once a CSS file is in scope for both.
export const TONE_PALETTE_VARS = {
  "--tone-peach-bg": "#ffd6a5",
  "--tone-mint-bg": "#a8e6cf",
  "--tone-mint-ink": "#0f3d2a",
  "--tone-mint-muted": "#215a42",
  "--tone-coral-bg": "#ffb4a2",
  "--tone-blue-bg": "#d7efff",
  "--tone-blue-ink": "#17324d",
  "--tone-blue-muted": "#496174",
} as React.CSSProperties;

export const DEFAULT_TONE_ORDER: Tone[] = ["purple", "peach", "blue", "mint", "coral", "lavender"];
